import { useState } from "react";
import { Download, Trash2, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Loading, Empty, Plate, ExportBtn, Field, NSelect, useFetch } from "@/components/common";
import { ConfirmDialog } from "@/components/DemoCleanup";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ImportWidget from "@/components/ImportWidget";
import { ComFilters } from "@/pages/commerciaux/ComDashboard";
import { download, fNum, fEur, fDate } from "@/lib/api";
import { ProviderChip, CardsTable, CardAssignDialog } from "@/components/FuelCards";
import { AdminPleins } from "@/components/Pleins";

const CLEAN_LABELS = { transactions: "Transactions Mooncard de démonstration", commerciaux: "Commerciaux fictifs", vehicules: "Véhicules commerciaux fictifs", releves: "Relevés fictifs liés", affectations: "Affectations fictives liées", entretiens: "Entretiens fictifs liés" };

function CleanupFuel({ onDone }) {
  const [open, setOpen] = useState(false);
  const s = useFetch("/fuel/demo/summary");
  const total = s.data ? Object.keys(CLEAN_LABELS).reduce((a, k) => a + s.data[k], 0) : 0;
  if (!s.data || !total) return null;
  const run = async () => {
    try {
      await api.delete("/fuel/demo", { params: { confirm: "NETTOYER" } });
      toast.success("Module Mooncard remis à zéro");
      setOpen(false);
      s.reload();
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="border-rose-300 text-rose-700" data-testid="fuel-demo-cleanup-button"><Trash2 className="w-4 h-4 mr-1" /> Nettoyer les données de démonstration</Button>
      {open && (
        <ConfirmDialog title="Remettre Mooncard / Carburant à zéro" onConfirm={run} onClose={() => setOpen(false)} label={`Supprimer ${total} enregistrement(s)`} testid="fuel-cleanup-dialog" wide>
          <p>Seules les données marquées DÉMO sont supprimées. Les commerciaux et véhicules réels, et les {s.data.real_transactions} transaction(s) réelle(s), ne sont pas touchés.</p>
          <ul className="text-sm" data-testid="fuel-cleanup-summary">{Object.entries(CLEAN_LABELS).map(([k, l]) => <li key={k} className="flex justify-between border-b py-1"><span>{l}</span><b className="text-rose-700">{s.data[k]}</b></li>)}</ul>
          {s.data.kept_used_elsewhere > 0 && <p className="text-xs">{s.data.kept_used_elsewhere} élément(s) fictif(s) conservé(s) car utilisé(s) ailleurs.</p>}
        </ConfirmDialog>
      )}
    </>
  );
}

function AssignDialog({ t, onClose, onDone }) {
  const coms = useFetch("/users", { role: "commercial", with_m2: true });
  const vehs = useFetch("/vehicles", { type: "commercial" });
  const [u, setU] = useState(t.suggested_user_id || "");
  const [v, setV] = useState(t.suggested_vehicle_id || "");
  const run = async () => {
    try {
      await api.patch(`/fuel/transactions/${t.id}/assign`, { user_id: u, vehicle_id: v });
      toast.success("Transaction affectée");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <ConfirmDialog title="Corriger l'affectation" onConfirm={run} onClose={onClose} label="Affecter" testid="fuel-assign-dialog" primary>
      <p>{fDate(t.date)} · {fEur(t.amount_ttc)} · fichier : « {t.commercial_name || "—"} » / « {t.plate || "—"} »</p>
      <Field label="Commercial"><NSelect value={u} onChange={setU} placeholder="Choisir…" testid="fuel-assign-user" options={(coms.data || []).filter((c) => !c.is_demo).map((c) => ({ value: c.id, label: c.name }))} /></Field>
      <Field label="Véhicule"><NSelect value={v} onChange={setV} placeholder="Choisir…" testid="fuel-assign-vehicle" options={(vehs.data || []).filter((x) => !x.is_demo).map((x) => ({ value: x.id, label: x.plate }))} /></Field>
    </ConfirmDialog>
  );
}

export function TxTable({ rows, showUser = true, onAssign, userLabel = "Commercial" }) {
  if (!rows.length) return <Empty text="Aucune transaction" />;
  return (
    <div className="overflow-x-auto max-h-[640px]">
      <table className="tbl w-full" data-testid="fuel-table">
        <thead className="sticky top-0">
          <tr><th>Date</th><th>Source</th>{showUser && <th>{userLabel}</th>}<th>Véhicule</th><th className="text-right">Km</th><th>Station</th><th>Carburant</th>
            <th className="text-right">Litres</th><th className="text-right">Prix/L</th><th className="text-right">HT</th><th className="text-right">TVA</th><th className="text-right">TTC</th><th>Réf.</th>{onAssign && <th></th>}</tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.id} data-testid={`fuel-row-${t.id}`}>
              <td>{fDate(t.date)}</td>
              <td><ProviderChip p={t.provider} /></td>
              {showUser && <td className={t.user_id ? "font-semibold" : "text-rose-600"}>{t.user_name}</td>}
              <td>{t.plate ? <Plate>{t.plate}</Plate> : "—"}</td>
              <td className="text-right tabular-nums">{fNum(t.mileage)}</td>
              <td>{t.station || "—"}</td><td>{t.fuel || "—"}</td>
              <td className="text-right tabular-nums">{fNum(t.liters, 2)}</td>
              <td className="text-right tabular-nums">{t.price_per_liter ? fNum(t.price_per_liter, 3) : "—"}</td>
              <td className="text-right tabular-nums">{fEur(t.amount_ht)}</td>
              <td className="text-right tabular-nums">{fEur(t.vat)}</td>
              <td className="text-right tabular-nums font-bold text-[#0E2D43]">{fEur(t.amount_ttc)}</td>
              <td className="font-mono text-[11px] text-slate-500">{t.reference || "—"}</td>
              {onAssign && <td><Button size="sm" variant="outline" onClick={() => onAssign(t)} data-testid={`fuel-assign-${t.id}`}><UserCheck className="w-4 h-4 mr-1" /> Affecter</Button></td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TxSummary({ rows, testid = "fuel-summary" }) {
  const total = rows.reduce((s, t) => s + (t.amount_ttc || 0), 0);
  const by = (p) => rows.filter((t) => (t.provider === "fleetpro") === (p === "fleetpro")).reduce((s, t) => s + (t.amount_ttc || 0), 0);
  const withL = rows.filter((t) => t.liters > 0);
  const totalL = withL.reduce((s, t) => s + (t.amount_ttc || 0), 0);
  const liters = withL.reduce((s, t) => s + t.liters, 0);
  return (
    <div className="flex flex-wrap gap-4 mb-3 text-sm" data-testid={testid}>
      <span><b className="text-[#0E2D43]">{rows.length}</b> transactions</span>
      <span>Total TTC <b className="text-[#0E2D43]" data-testid={`${testid}-total`}>{fEur(total)}</b></span>
      <span>Mooncard <b className="text-[#9a5a05]" data-testid={`${testid}-mooncard`}>{fEur(by("mooncard"))}</b></span>
      <span>Fleet Pro <b className="text-[#1d5fa8]" data-testid={`${testid}-fleetpro`}>{fEur(by("fleetpro"))}</b></span>
      <span><b className="text-[#0E2D43]">{fNum(liters, 1)}</b> L</span>
      <span>Prix moyen pondéré <b className="text-[#0E2D43]">{liters ? `${fNum(totalL / liters, 3)} €/L` : "—"}</b></span>
    </div>
  );
}

function TxList({ f, testid, scope = "commercial", userLabel }) {
  const { data, loading } = useFetch("/fuel/transactions", { ...f, scope });
  const rows = data || [];
  return (
    <>
      <TxSummary rows={rows} testid={`${testid}-summary`} />
      <div className="bg-white border border-slate-200 rounded-xl rise" data-testid={testid}>{loading ? <Loading /> : <TxTable rows={rows} userLabel={userLabel} />}</div>
    </>
  );
}

export default function Mooncard() {
  const [f, setF] = useState({});
  const [k, setK] = useState(0);
  const review = useFetch("/fuel/transactions", { unmatched: true });
  const [assign, setAssign] = useState(null);
  const refresh = () => {
    setK((x) => x + 1);
    review.reload();
  };
  const done = () => { setAssign(null); refresh(); };
  return (
    <div data-testid="mooncard-page">
      <PageHeader overline="Véhicules commerciaux" title="Carburant">
        <CleanupFuel onDone={refresh} />
        <ExportBtn path="/export/fuel" params={f} filename="transactions-carburant.xlsx" testid="export-fuel" />
      </PageHeader>
      {assign && (assign.provider === "fleetpro" ? <CardAssignDialog t={assign} onClose={() => setAssign(null)} onDone={done} /> : <AssignDialog t={assign} onClose={() => setAssign(null)} onDone={done} />)}
      <Tabs defaultValue="tx">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="tx" data-testid="tab-transactions">Toutes les transactions</TabsTrigger>
          <TabsTrigger value="mooncard" data-testid="tab-mooncard">Mooncard</TabsTrigger>
          <TabsTrigger value="fleetpro" data-testid="tab-fleetpro">Fleet Pro</TabsTrigger>
          <TabsTrigger value="pleins" data-testid="tab-pleins">Pleins saisis (contrôle)</TabsTrigger>
          <TabsTrigger value="review" data-testid="tab-review">À vérifier ({review.data?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import">Imports</TabsTrigger>
        </TabsList>
        <TabsContent value="tx">
          <ComFilters f={f} setF={setF} provider testid="fuel-filters" />
          <TxList key={`all-${k}`} f={f} testid="fuel-all" />
        </TabsContent>
        <TabsContent value="mooncard">
          <p className="text-sm text-slate-500 mb-3">Historique Mooncard conservé intégralement (cartes utilisées jusqu'au passage à Fleet Pro).</p>
          <ComFilters f={f} setF={setF} testid="fuel-filters-mooncard" />
          <TxList key={`mc-${k}`} f={{ ...f, provider: "mooncard" }} testid="fuel-mooncard" />
        </TabsContent>
        <TabsContent value="fleetpro" className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl"><div className="px-4 pt-3 font-display font-bold text-[#0E2D43]">Cartes Fleet Pro</div><CardsTable testid="fleetpro-cards" /></div>
          <ComFilters f={f} setF={setF} testid="fuel-filters-fleetpro" />
          <TxList key={`fp-${k}`} f={{ ...f, provider: "fleetpro" }} testid="fuel-fleetpro" />
          <div className="font-display font-bold text-[#0E2D43] pt-2">Cartes des titulaires administratifs (Direction)</div>
          <TxList key={`fpa-${k}`} f={{ provider: "fleetpro" }} scope="administratif" userLabel="Titulaire" testid="fuel-fleetpro-admin" />
        </TabsContent>
        <TabsContent value="pleins"><AdminPleins /></TabsContent>
        <TabsContent value="review">
          <p className="text-sm text-slate-500 mb-3">Transactions importées (Mooncard ou Fleet Pro) qui n'ont pas pu être rapprochées avec certitude. Elles ne sont comptées dans aucune analyse tant qu'elles ne sont pas affectées : aucune affectation n'est inventée.</p>
          <div className="bg-white border border-slate-200 rounded-xl" data-testid="fuel-review-list">{review.loading ? <Loading /> : <TxTable rows={review.data || []} onAssign={setAssign} />}</div>
        </TabsContent>
        <TabsContent value="import" className="max-w-4xl space-y-4">
          <button onClick={() => download("/imports/template/fleetpro", { fmt: "xlsx" }, "modele-fleet-pro.xlsx")} className="inline-flex items-center gap-2 text-sm font-semibold text-[#E0830B] hover:underline" data-testid="download-fleetpro-template">
            <Download className="w-4 h-4" /> Télécharger le modèle Fleet Pro (Excel)
          </button>
          <ImportWidget
            endpoint="/fleetpro/import"
            title="Import Fleet Pro (Excel / CSV)"
            hint="Colonnes reconnues automatiquement : date, numéro de carte, titulaire, immatriculation, kilométrage, station, carburant, litres, prix au litre, HT, TVA, TTC, référence. Rapprochement par numéro de carte puis par titulaire (JULIEN BIANCO → Julien Bianco, MICHAEL → Mickael…). Si la correspondance est incertaine, ou si le titulaire commercial n'a pas de véhicule affecté à la date, la transaction va dans « À vérifier ». Les colonnes de code PIN sont ignorées. Fichier déjà importé refusé, doublons ignorés."
            previewCols={["date", "commercial_name", "user_name", "plate", "mileage", "station", "liters", "price_per_liter", "amount_ttc", "reference"]}
            onDone={refresh}
            testid="fleetpro-import"
          />
          <hr className="border-slate-200" />
          <button onClick={() => download("/imports/template/mooncard", { fmt: "xlsx" }, "modele-mooncard.xlsx")} className="inline-flex items-center gap-2 text-sm font-semibold text-[#E0830B] hover:underline" data-testid="download-fuel-template">
            <Download className="w-4 h-4" /> Télécharger le modèle (Excel) — colonnes détaillées dans Paramètres → Imports
          </button>
          <ImportWidget
            endpoint="/fuel/import"
            title="Import Mooncard (Excel / CSV)"
            hint="Colonnes reconnues automatiquement : date, porteur/commercial, immatriculation, kilométrage, station, carburant, litres, prix au litre, HT, TVA, TTC, référence. Un fichier déjà importé est refusé et les doublons (même référence, ou même date + véhicule + montant + litres) sont ignorés. Rapprochement par immatriculation (affectation à la date) puis par nom ; sans commercial ET véhicule réels certains, la transaction va dans « À vérifier »."
            previewCols={["date", "plate", "commercial_name", "user_name", "mileage", "station", "liters", "price_per_liter", "amount_ttc", "reference"]}
            onDone={refresh}
            testid="fuel-import"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
